import type { IconName } from '@raiadrogasil/pulso-icons'
import { useCallback, useState } from 'react'
import { Icon } from '~/components/icon'
import {
  optionMenuItemButtonVariants,
  optionMenuItemContainerVariants,
} from './index.styles'

export interface ContentProps {
  prefixIcon?: IconName
  label: string
  description?: string
  value: string
}

export interface ItemProps {
  type?: 'single' | 'multi'
  content: ContentProps
  checked?: boolean
  disabled?: boolean
  testID?: string
  onPress?: () => void
}

export function Item({
  type = 'single',
  content,
  checked,
  disabled,
  testID,
  onPress,
}: ItemProps) {
  const [hasPressed, setHasPressed] = useState(false)

  const hasPrefixIcon = !!content.prefixIcon
  const hasDescription = !!content.description

  const handlePressIn = useCallback(() => setHasPressed(true), [])
  const handlePressOut = useCallback(() => setHasPressed(false), [])

  const truncateLabel = (label?: string) => {
    const isPrefixIconWidth = hasPrefixIcon ? 23 : 25
    if (label && label.length > isPrefixIconWidth) {
      return `${label.substr(0, isPrefixIconWidth)}...`
    }
    return label
  }

  return (
    <button
      onMouseDown={handlePressIn}
      onMouseUp={handlePressOut}
      onClick={onPress}
      data-testid={testID}
      disabled={disabled}
      type="button"
      className={optionMenuItemButtonVariants()}
    >
      <div className={optionMenuItemContainerVariants({ type })}>
        {type === 'multi' && (
          <div className="flex items-center p-onepulse">
            <Icon
              symbol={
                checked
                  ? 'rdicon-checkbox-checked'
                  : 'rdicon-checkbox-unchecked'
              }
              color={
                disabled
                  ? 'colorTextNeutralDisabled'
                  : checked
                    ? 'colorActionFillBrandPrimaryEnabled'
                    : 'colorTextNeutralDefault'
              }
              size="small"
            />
          </div>
        )}

        {hasPrefixIcon && (
          <Icon
            symbol={content.prefixIcon}
            color={
              disabled ? 'colorTextNeutralDisabled' : 'colorTextNeutralDefault'
            }
            size="small"
          />
        )}

        <div className="flex flex-1 flex-col items-start gap-halfpulse p-onepulse ">
          <p
            className={`tracking font-rdmodern font-regular text-threeandhalfpulse leading-small ${
              disabled ? 'text-text-neutral-disabled' : 'text-text-neutral'
            }`}
          >
            {content.label}
          </p>
          {hasDescription && (
            <p
              className={`tracking font-rdmodern font-regular text-threepulse leading-small ${
                disabled
                  ? 'text-text-neutral-disabled'
                  : 'text-text-neutral-placeholder'
              }`}
            >
              {truncateLabel(content.description)}
            </p>
          )}
        </div>

        {type === 'single' && checked && (
          <Icon
            symbol="rdicon-checkmark"
            color="colorTextSuccessAlternative"
            size="small"
          />
        )}
      </div>
    </button>
  )
}
